import _ from "lodash";
import ftApiClient from "./ftApiClient";
import wtApiClient from "./wtApiClient";
import qs from "query-string";
import { filterConstants } from "@constants";
import { IOption } from "@interfaces";
import { filterProducts } from "@utilities";

const { ACCOMMODATION, ACTIVITIES, TRANSPORTS, SUPPLIERS, LENGTH_OF_TRIP, DESTINATIONS, START_LOCATIONS } = filterConstants;

export const getCategoryMap = (searchCategories: any) =>
    ({
        [ACCOMMODATION]: searchCategories.categories.accommodation,
        [ACTIVITIES]: searchCategories.categories.activities,
        [SUPPLIERS]: searchCategories.categories.suppliers,
        [TRANSPORTS]: searchCategories.categories.transports,
        [LENGTH_OF_TRIP]: searchCategories.categories.duration,
        [DESTINATIONS]: searchCategories.categories.destinations,
        [START_LOCATIONS]: searchCategories.categories.startLocations,
    }) as { [key: string]: IOption[] };

export interface ISearchFilterItem {
    value: string;
    count: number;
}

export interface ISearchFiltersResponse {
    accommodation: ISearchFilterItem[];
    activity: ISearchFilterItem[];
    transport: ISearchFilterItem[];
    supplier: ISearchFilterItem[];
    destination: ISearchFilterItem[];
    start_location: ISearchFilterItem[];
    country: ISearchFilterItem[];
    duration: ISearchFilterItem[];
}

export const mapSearchFilterToOptions = (items: ISearchFilterItem[]): IOption[] =>
    items.map((item) => ({
        id: item.value,
        text: item.value,
    }));

export interface IFTSearchParams {
    q?: string;
    activity?: string;
    accommodation?: string;
    transport?: string;
    supplier?: string;
    destination?: string;
    start_location?: string;
    country?: string;
    duration?: string;
    page?: number;
    per_page?: number;
    sort_by?: string;
    sort_direction?: string;
}

export interface IFTSearchResponse {
    products: any[];
    pagination: {
        current_page: number;
        per_page: number;
        total: number;
        last_page: number;
    };
    filters_applied: Record<string, string>;
}

/** A single row from `GET /accommodation/countries`. */
export interface IAccommodationCountry {
    accommCountryId: number;
    accommCountryName: string;
}

/** A single row from `GET /accommodation/states/{accommCountryId}`. */
export interface IAccommodationState {
    accommStateId: number;
    accommStateName: string;
}

/**
 * Params accepted by `categoriesApi.searchAccommodation`. All fields are
 * optional — the caller is expected to pass at least a country id (or a
 * keyword) so the backend has something to narrow on. The country/state
 * ids belong to the accommodation backend's OWN id namespace and must be
 * sourced from `getAccommodationCountries` / `getAccommodationStates` —
 * they are NOT the TDMS location-tree ids used by the other tabs.
 */
export interface IAccommodationSearchParams {
    /** accommCountryId from /accommodation/countries (NOT the TDMS country id). */
    countries?: number | string;
    /** accommStateId from /accommodation/states/{accommCountryId}. Required by backend. */
    states?: number | string;
    /** @deprecated The accommodation search endpoint does not accept region. */
    regions?: number | string;
    /** @deprecated The accommodation search endpoint does not accept locality. */
    locality?: number | string;
    /**
     * @deprecated The `availableAccommodationProductsByLocation` endpoint
     * rejects a free-text `q` param (400). Kept for caller compatibility but
     * NOT forwarded — keyword filtering is done client-side on the results.
     */
    q?: string;
    limit?: number;
    /** YYYY-MM-DD. Required by the backend — defaulted by the caller when unknown. */
    checkInDate?: string;
    /** YYYY-MM-DD. Required by the backend. Must be > checkInDate. */
    checkOutDate?: string;
}

export const categoriesApi = {
    searchProducts: (params: IFTSearchParams) => {
        const query = qs.stringify(
            _.pickBy(params, (v) => v !== undefined && v !== null && v !== ""),
        );
        const url = `/products/search${query ? `?${query}` : ""}`;
        return ftApiClient.get<{ data: IFTSearchResponse }>(url);
    },
    /**
     * Accommodation product search used by the Trip Planner's
     * "Accommodation" sidebar tab. Hits the wt endpoint
     * `GET /accommodation/availableAccommodationProductsByLocation` with
     * `accommCountry`, `accommState`, `checkInDate`, `checkOutDate`.
     *
     * This endpoint (unlike the older `/accommodation/search`) returns the
     * live, bookable room rates — each fare carries the booking keys
     * (`defaultRateCode`, `roomCode`, `externalId`, `externalSource`)
     * needed to actually book the room downstream at checkout. The
     * response shape matches the search endpoint (products under
     * `data.results`), so the caller still normalises via
     * `extractProducts`.
     *
     * Param constraints verified against the live wt backend:
     *  - `accommState` is REQUIRED (country-only requests 400).
     *  - `checkInDate` / `checkOutDate` are REQUIRED.
     *  - a free-text `q` keyword param is REJECTED (400) — keyword
     *    filtering must be done client-side on the results.
     *  - `records-start` / `records-length` ARE accepted.
     */
    searchAccommodation: (params: IAccommodationSearchParams) => {
        // The accommodation backend has its OWN id namespace, distinct
        // from TDMS country/state/region/locality. Only accommCountry +
        // accommState are accepted; accommRegion / accommLocality (and a
        // free-text `q`) are rejected by the validator (400 "field was not
        // expected"). Source the ids from getAccommodationCountries /
        // getAccommodationStates — never the cached TDMS location tree.
        const query = qs.stringify(
            _.pickBy(
                {
                    accommCountry: params.countries,
                    accommState: params.states,
                    checkInDate: params.checkInDate,
                    checkOutDate: params.checkOutDate,
                    "records-start": 0,
                    "records-length": params.limit ?? 20,
                },
                (v) => v !== undefined && v !== null && v !== "",
            ),
        );
        const url = `/accommodation/availableAccommodationProductsByLocation${
            query ? `?${query}` : ""
        }`;
        return wtApiClient.get<{ data: IFTSearchResponse }>(url);
    },
    /**
     * Accommodation country list. Returns the accommodation backend's own
     * country ids (`accommCountryId`) which feed `searchAccommodation` and
     * `getAccommodationStates`. The response is a flat array.
     */
    getAccommodationCountries: () => {
        const url = "/accommodation/countries";
        return wtApiClient.get<IAccommodationCountry[]>(url);
    },
    /**
     * Accommodation "state" list for a given accommodation country id.
     * These are city/region-level entries (e.g. "Cairns - QLD") in the
     * accommodation backend's own id namespace (`accommStateId`). The
     * response is a flat array.
     */
    getAccommodationStates: (accommCountryId: number | string) => {
        const url = `/accommodation/states/${encodeURIComponent(accommCountryId)}`;
        return wtApiClient.get<IAccommodationState[]>(url);
    },
    getSearchFilters: () => {
        const url = "/products/search/filters";
        return ftApiClient.get<{ data: ISearchFiltersResponse }>(url);
    },
    getCategories: () => {
        const url = "/product/feed/categories";
        return ftApiClient.get(url);
    },
    getHomepageProduct: (params: {
        chipType: string;
        selectedLocations?: {
            country?: { id: string } | null | undefined;
            state?: { id: string } | null | undefined;
            region?: { id: string } | null | undefined;
            locality?: { id: string } | null | undefined;
        };
    }) => {
        const countryId = params.selectedLocations?.country?.id;
        const stateId = params.selectedLocations?.state?.id;
        const regionId = params.selectedLocations?.region?.id;
        const localityId = params.selectedLocations?.locality?.id;
        const locationParams = _.pickBy(
            { countryId, stateId, regionId, localityId },
            (val) => val !== undefined && val !== null && val !== "",
        );
        const queryString = qs.stringify({
            ...locationParams,
            filterBy: params.chipType,
            sortDirection: "asc",
            sortBy: "ranking",
            data: "brief",
        });
        const url = `/products/home/v2?${queryString}`;
        return ftApiClient.get(url).then((response) => {
            if (response.data?.data && Array.isArray(response.data.data)) {
                response.data.data = response.data.data.map((item: any) => {
                    if (item.products && Array.isArray(item.products)) {
                        item.products = filterProducts(item.products);
                    }
                    return item;
                });
            }
            return response;
        });
    },

    getHomepageProductByUser: (
        countries: number | string,
        states?: number | string,
        regions?: number | string,
        locality?: number | string,
        filters?: { [categoryType: string]: Array<number | string> },
        extraOptions?: { "records-start"?: number; "records-lengths"?: number; [key: string]: any },
    ) => {
        const locationParams = _.pickBy(
            { countries, states, regions, locality },
            (val) => val !== undefined && val !== null && val !== "" && val !== 0,
        );
        const filterParams = _(filters)
            .mapValues((vals) => (_.isArray(vals) && vals.length ? vals.join(",") : undefined))
            .pickBy((val) => val !== undefined)
            .value() as Record<string, string>;
        const allParams = _.assign(
            {},
            { "records-start": "0", "records-length": 10 },
            locationParams,
            filterParams,
            extraOptions,
        );
        const queryString = qs.stringify(allParams) || "all=\\s";
        return wtApiClient
            .get(`/products?${queryString}&sortDirection=asc&sortBy=ranking`)
            .then((response) => {
                if (response.data?.results) {
                    response.data.results = filterProducts(response.data.results);
                }
                return response;
            });
    },

    getSchema: () => {
        const url = "/product/feed/schema";
        return ftApiClient.get(url);
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getCountries: (stateId?: number | string, paramType?: string) => {
        const url = "/categories/countries";
        return wtApiClient.get(url);
    },
    getStates: (countriesId?: number | string, paramType?: string) => {
        let url = "/categories/states";
        if (countriesId && paramType) {
            url += `?${encodeURIComponent(paramType)}=${encodeURIComponent(countriesId)}`;
        }
        return wtApiClient.get(url);
    },
    getRegions: (stateId?: number | string, paramType?: string) => {
        let url = "/categories/regions";
        if (stateId && paramType) {
            url += `?${encodeURIComponent(paramType)}=${encodeURIComponent(stateId)}`;
        }
        return wtApiClient.get(url);
    },
    getTowns: (regionId?: number | string, paramType?: string) => {
        let url = "/categories/locality";
        if (regionId && paramType) {
            url += `?${encodeURIComponent(paramType)}=${encodeURIComponent(regionId)}`;
        }
        return wtApiClient.get(url);
    },
    getDestinationProductsByRegion: (regionId: number | string, countryId: number | string) => {
        const url = `/products?regions=${regionId}&countries=${countryId}&sortDirection=asc&sortBy=ranking`;
        return wtApiClient.get(url).then((response) => {
            if (response.data?.results) {
                response.data.results = filterProducts(response.data.results);
            }
            return response;
        });
    },

    getCategoryMap,
};
// https://freelancetest02.websitetravel.com/apiv1/categories/suppliers
// https://freelancetest02.websitetravel.com/apiv1/categories/transport
// https://freelancetest02.websitetravel.com/apiv1/categories/suppliers?countries=20
// https://freelancetest02.websitetravel.com/apiv1/categories/transport?countries=20
